import { useTranslation } from "react-i18next";
import { Clock, type LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "../../components/ui/button/index.js";
import { cn } from "../../lib/utils.js";
import { CampaignReviewGroup } from "./CampaignReviewGroup.js";
import { CampaignSendIcon } from "./CampaignSendIcon.js";

type DeliveryMode = "now" | "schedule";

function TimingOption({
  value,
  selected,
  icon,
  title,
  subtitle,
  testId,
  onSelect,
  children,
}: {
  value: DeliveryMode;
  selected: boolean;
  icon: ReactNode;
  title: string;
  subtitle: string;
  testId: string;
  onSelect: (mode: DeliveryMode) => void;
  children?: ReactNode;
}): React.JSX.Element {
  return (
    <div
      className={cn(
        "grid min-w-0 content-start gap-2 rounded-lg border px-3 py-2.5 transition-colors has-[input:focus-visible]:ring-[3px] has-[input:focus-visible]:ring-[var(--color-ring)]",
        selected
          ? "border-foreground/80 bg-surface-muted shadow-[0_1px_2px_rgba(15,23,42,0.06)]"
          : "border-line/70 bg-surface hover:bg-surface-muted/40",
      )}
    >
      <label className="relative flex min-w-0 cursor-pointer items-start gap-2.5" data-testid={testId}>
        <input
          type="radio"
          name="campaign-delivery-mode"
          value={value}
          checked={selected}
          onChange={() => onSelect(value)}
          className="sr-only"
        />
        <span className="grid min-w-0 gap-0.5">
          <span
            className={cn(
              "flex items-center gap-1.5 text-[0.8125rem] font-semibold",
              selected ? "text-foreground" : "text-muted",
            )}
          >
            {icon}
            {title}
          </span>
          <span className="text-[0.75rem] text-muted">{subtitle}</span>
        </span>
      </label>
      {children}
    </div>
  );
}

function OptionIcon({ icon: Icon }: { icon: LucideIcon }): React.JSX.Element {
  return <Icon size={14} className="shrink-0" aria-hidden="true" />;
}

export function CampaignDeliveryTiming({
  deliveryMode,
  onDeliveryModeChange,
  scheduleChosen,
  onOpenSchedule,
  launchHint,
  launchHintId,
  launchError,
}: {
  deliveryMode: DeliveryMode;
  onDeliveryModeChange: (mode: DeliveryMode) => void;
  scheduleChosen: boolean;
  onOpenSchedule: () => void;
  launchHint: string | null;
  launchHintId: string;
  launchError: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="grid gap-2" data-testid="campaign-launch-panel">
      <CampaignReviewGroup
        titleId="campaign-when-to-send-title"
        title={t("campaigns.review.launch.whenToSend")}
      >
        <div
          className="grid gap-2 sm:grid-cols-2"
          role="radiogroup"
          aria-labelledby="campaign-when-to-send-title"
        >
          <TimingOption
            value="now"
            selected={deliveryMode === "now"}
            icon={<CampaignSendIcon />}
            title={t("campaigns.review.launch.sendNowOption")}
            subtitle={t("campaigns.review.launch.sendNowSubtitle")}
            testId="campaign-delivery-send-now"
            onSelect={onDeliveryModeChange}
          />
          <TimingOption
            value="schedule"
            selected={deliveryMode === "schedule"}
            icon={<OptionIcon icon={Clock} />}
            title={t("campaigns.review.launch.sendLater")}
            subtitle={t("campaigns.review.launch.sendLaterSubtitle")}
            testId="campaign-delivery-schedule"
            onSelect={onDeliveryModeChange}
          >
            {deliveryMode === "schedule" ? (
              <div className="border-t border-line/60 pt-2 ps-[1.625rem]">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={onOpenSchedule}
                  data-testid={scheduleChosen ? "campaign-schedule-change" : "campaign-schedule-trigger"}
                >
                  <Clock size={14} aria-hidden="true" />
                  {scheduleChosen
                    ? t("campaigns.review.launch.changeSchedule")
                    : t("campaigns.review.launch.chooseDateTime")}
                </Button>
              </div>
            ) : null}
          </TimingOption>
        </div>
      </CampaignReviewGroup>

      {launchHint ? (
        <p className="m-0 px-1 text-[0.75rem] text-muted" id={launchHintId} data-testid="campaign-launch-hint">
          {launchHint}
        </p>
      ) : null}

      {launchError ? (
        <p className="m-0 px-1 text-[0.75rem] text-error" role="alert">
          {launchError}
        </p>
      ) : null}
    </div>
  );
}
